/**
 * The Events workspace's sidebar: a project picker (like LogSnag's project
 * switcher), the project's pages, its channels with counts, then your push
 * settings and (admins) Settings.
 */
import { useMemo } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Bell, ChevronsUpDown, Gauge, Hash, Inbox, Layers, Map as MapIcon, Settings2, Users } from "lucide-react"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/useAuth"
import { useEventProject, useEventProjects } from "@/hooks/use-events"
import { parseEventsPath, readLastProject } from "@/lib/events"
import { cn } from "@/lib/utils"
import { isAdmin } from "@/lib/roles"

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function compact(n: number) {
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
}

export function EventsSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const route = parseEventsPath(location.pathname)
  const channel = new URLSearchParams(location.search).get("channel") ?? ""
  // Pages without a project in the path (an event, settings) keep the last one.
  const project = route.project ?? (route.section === "feed" ? null : readLastProject(safeLocalStorage()))
  const projects = useEventProjects()
  const detail = useEventProject(project)
  const current = useMemo(() => (projects.data ?? []).find((p) => p.slug === project) ?? null, [projects.data, project])
  const base = project ? `/events/${project}` : "/events"
  const onFeed = route.section === "feed"

  return (
    <>
      <SidebarGroup className="py-1">
        <SidebarGroupContent>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-full items-center gap-2 rounded-lg px-2 text-left text-sm transition-colors hover:bg-black/[0.05] data-[state=open]:bg-black/[0.05] dark:hover:bg-white/[0.06] group-data-[collapsible=icon]:hidden"
              >
                <span aria-hidden className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/[0.05] text-[13px] dark:bg-white/[0.08]">
                  {current?.icon ?? (project ? "#" : <Layers className="h-3.5 w-3.5" />)}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium">{current?.name ?? project ?? "All projects"}</span>
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[232px] p-1.5" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuLabel className="px-2 pb-1 pt-0.5 text-xs font-medium text-muted-foreground">Projects</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => navigate("/events")} className={cn("gap-2 rounded-lg", !project && "bg-muted")}>
                <Layers className="h-4 w-4" /> All projects
              </DropdownMenuItem>
              {(projects.data ?? []).map((p) => (
                <DropdownMenuItem key={p.id} onSelect={() => navigate(`/events/${p.slug}`)} className={cn("gap-2 rounded-lg", p.slug === project && "bg-muted")}>
                  <span aria-hidden className="w-4 text-center">
                    {p.icon ?? "#"}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{compact(p.event_count)}</span>
                </DropdownMenuItem>
              ))}
              {isAdmin(user) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate("/events/settings")} className="gap-2 rounded-lg">
                    <Settings2 className="h-4 w-4" /> Manage projects
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <SidebarMenu className="mt-1">
            <NavItem to={base} label="Feed" icon={<Inbox />} active={onFeed && !channel} />
            <NavItem to={`${base}/map`} label="Map" icon={<MapIcon />} active={route.section === "map"} />
            {project && (
              <>
                <NavItem to={`${base}/insights`} label="Insights" icon={<Gauge />} active={route.section === "insights"} />
                <NavItem to={`${base}/users`} label="Users" icon={<Users />} active={route.section === "users" || route.section === "user"} />
              </>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {project && (detail.data?.channels.length ?? 0) > 0 && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel>Channels</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {detail.data!.channels.map((c) => (
                <SidebarMenuItem key={c.id}>
                  <SidebarMenuButton asChild isActive={onFeed && channel === c.slug} tooltip={`#${c.slug}`}>
                    <Link to={`${base}?channel=${encodeURIComponent(c.slug)}`}>
                      <Hash />
                      <span className="font-mono text-[13px]">{c.slug}</span>
                    </Link>
                  </SidebarMenuButton>
                  {c.event_count > 0 && <SidebarMenuBadge className="tabular-nums text-muted-foreground">{compact(c.event_count)}</SidebarMenuBadge>}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      <SidebarGroup className="mt-auto py-1">
        <SidebarGroupContent>
          <SidebarMenu>
            <NavItem to="/events/notifications" label="Notifications" icon={<Bell />} active={route.section === "notifications"} />
            {isAdmin(user) && <NavItem to="/events/settings" label="Events settings" icon={<Settings2 />} active={route.section === "settings"} />}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  )
}

function NavItem({ to, label, icon, active }: { to: string; label: string; icon: React.ReactNode; active: boolean }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={label}>
        <Link to={to}>
          {icon}
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
